/**
 * Change Detection Utility
 * Provides methods for detecting and analyzing changes between data objects
 */

/**
 * Deep comparison of two objects to detect changes
 * @param {object} before - The original object
 * @param {object} after - The modified object
 * @returns {object} - Change analysis result
 */
export const detectChanges = (before, after) => {
  const changes = {
    hasChanges: false,
    fieldsChanged: [],
    fieldChanges: {},
    addedFields: [],
    removedFields: [],
    statistics: {
      totalFields: 0,
      changedFields: 0,
      unchangedFields: 0
    }
  };

  // Handle null/undefined cases
  if (!before && !after) return changes;
  if (!before) before = {};
  if (!after) after = {};

  // Get all unique keys from both objects
  const beforeKeys = Object.keys(before);
  const afterKeys = Object.keys(after);
  const allKeys = new Set([...beforeKeys, ...afterKeys]);

  changes.statistics.totalFields = allKeys.size;

  allKeys.forEach(key => {
    const beforeValue = before[key];
    const afterValue = after[key];

    // Check if field was added
    if (!(key in before) && (key in after)) {
      changes.addedFields.push(key);
      changes.fieldsChanged.push(key);
      changes.fieldChanges[key] = {
        type: 'added',
        before: undefined,
        after: afterValue,
        dataType: typeof afterValue
      };
      changes.hasChanges = true;
      changes.statistics.changedFields++;
      return;
    }

    // Check if field was removed
    if ((key in before) && !(key in after)) {
      changes.removedFields.push(key);
      changes.fieldsChanged.push(key);
      changes.fieldChanges[key] = {
        type: 'removed',
        before: beforeValue,
        after: undefined,
        dataType: typeof beforeValue
      };
      changes.hasChanges = true;
      changes.statistics.changedFields++;
      return;
    }

    // Check if field was modified
    if (!deepEqual(beforeValue, afterValue)) {
      changes.fieldsChanged.push(key);
      changes.fieldChanges[key] = {
        type: 'modified',
        before: beforeValue,
        after: afterValue,
        dataType: typeof afterValue,
        changeType: getChangeType(beforeValue, afterValue)
      };
      changes.hasChanges = true;
      changes.statistics.changedFields++;
    } else {
      changes.statistics.unchangedFields++;
    }
  });

  return changes;
};

/**
 * Deep equality check for two values
 * @param {any} a - First value
 * @param {any} b - Second value
 * @returns {boolean} - True if values are deeply equal
 */
export const deepEqual = (a, b) => {
  if (a === b) return true;
  
  if (a == null || b == null) return a === b;
  
  if (typeof a !== typeof b) return false;
  
  if (typeof a !== 'object') return a === b;
  
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  
  if (keysA.length !== keysB.length) return false;
  
  for (let key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }
  
  return true;
};

/**
 * Determine the type of change for a field
 * @param {any} before - Original value
 * @param {any} after - New value
 * @returns {string} - Type of change
 */
export const getChangeType = (before, after) => {
  const beforeType = typeof before;
  const afterType = typeof after;
  
  if (beforeType !== afterType) return 'type_change';
  
  if (beforeType === 'string') {
    if (before === '' && after !== '') return 'filled';
    if (before !== '' && after === '') return 'cleared';
    if (before.length < after.length) return 'expanded';
    if (before.length > after.length) return 'truncated';
    return 'modified';
  }
  
  if (beforeType === 'number') {
    if (before < after) return 'increased';
    if (before > after) return 'decreased';
    return 'modified';
  }
  
  if (beforeType === 'boolean') return 'toggled';
  
  return 'modified';
};

/**
 * Get human-readable description of changes
 * @param {object} changes - Change detection result
 * @returns {string} - Human-readable description
 */
export const getChangeSummary = (changes) => {
  if (!changes.hasChanges) return 'No changes detected';
  
  const parts = [];
  
  if (changes.addedFields.length > 0) {
    parts.push(`Added ${changes.addedFields.length} field(s): ${changes.addedFields.join(', ')}`);
  }
  
  if (changes.removedFields.length > 0) {
    parts.push(`Removed ${changes.removedFields.length} field(s): ${changes.removedFields.join(', ')}`);
  }
  
  const modifiedFields = changes.fieldsChanged.filter(
    field => !changes.addedFields.includes(field) && !changes.removedFields.includes(field)
  );
  
  if (modifiedFields.length > 0) {
    parts.push(`Modified ${modifiedFields.length} field(s): ${modifiedFields.join(', ')}`);
  }
  
  return parts.join('; ');
};

/**
 * Filter out unchanged fields from an object
 * @param {object} obj - Object to filter
 * @param {array} changedFields - List of changed field names
 * @returns {object} - Object containing only changed fields
 */
export const getChangedFieldsOnly = (obj, changedFields) => {
  const result = {};
  changedFields.forEach(field => {
    if (field in obj) {
      result[field] = obj[field];
    }
  });
  return result;
};

/**
 * Create a diff object suitable for displaying changes
 * @param {object} changes - Change detection result
 * @returns {array} - Array of diff objects for UI display
 */
export const createDisplayDiff = (changes) => {
  return changes.fieldsChanged.map(field => {
    const fieldChange = changes.fieldChanges[field];
    return {
      field,
      type: fieldChange.type,
      before: fieldChange.before,
      after: fieldChange.after,
      changeType: fieldChange.changeType,
      displayBefore: formatValueForDisplay(fieldChange.before),
      displayAfter: formatValueForDisplay(fieldChange.after)
    };
  });
};

/**
 * Format a value for user-friendly display
 * @param {any} value - Value to format
 * @returns {string} - Formatted value
 */
export const formatValueForDisplay = (value) => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (value === '') return '(empty)';
  if (typeof value === 'string') return `"${value}"`;
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value)) return `[${value.length} items]`;
  if (typeof value === 'object') return '{object}';
  return String(value);
};

/**
 * Check if changes are significant enough to log
 * @param {object} changes - Change detection result
 * @param {object} options - Options for significance check
 * @returns {boolean} - True if changes are significant
 */
export const areChangesSignificant = (changes, options = {}) => {
  const {
    ignoreFields = ['updatedAt', 'lastModified', 'timestamp'],
    minimumFields = 1,
    significantFields = ['price', 'status', 'clientName', 'productName']
  } = options;
  
  // Filter out ignored fields
  const significantChangedFields = changes.fieldsChanged.filter(
    field => !ignoreFields.includes(field)
  );
  
  // Check if minimum number of fields changed
  if (significantChangedFields.length < minimumFields) return false;
  
  // Check if any significant fields changed
  const hasSignificantFieldChanges = significantChangedFields.some(
    field => significantFields.includes(field)
  );
  
  return hasSignificantFieldChanges;
};

/**
 * Calculate change statistics for reporting
 * @param {array} changeLogs - Array of change logs
 * @returns {object} - Statistics about changes
 */
export const calculateChangeStatistics = (changeLogs) => {
  const stats = {
    totalChanges: changeLogs.length,
    fieldFrequency: {},
    changeTypes: {},
    userActivity: {},
    timeDistribution: {}
  };
  
  changeLogs.forEach(log => {
    // Count field changes
    if (log.changes && log.changes.fieldsChanged) {
      log.changes.fieldsChanged.forEach(field => {
        stats.fieldFrequency[field] = (stats.fieldFrequency[field] || 0) + 1;
      });
    }
    
    // Count change types
    stats.changeTypes[log.action] = (stats.changeTypes[log.action] || 0) + 1;
    
    // Count user activity
    stats.userActivity[log.userEmail] = (stats.userActivity[log.userEmail] || 0) + 1;
    
    // Count by time (hour of day)
    if (log.timestamp) {
      const hour = new Date(log.timestamp.toDate()).getHours();
      stats.timeDistribution[hour] = (stats.timeDistribution[hour] || 0) + 1;
    }
  });
  
  return stats;
};
